import { Container } from '../../../odie';

import {
  BlockEntity,
  BombBlockEntity,
  RocketBlockEntity,
  setBlockEntityViewLayer,
} from '../../entities/BlockEntity';
import { IEffect } from './IEffect';
import { gsap } from 'gsap';
import { app } from '../../app';
import { mapGetPan } from '../../util/mapTools';
import { tweenPower2In } from '../../../lib/util/tweens';
import { BlockId } from '../../defs/block';
import { GameScene } from '../../GameScene';

// constants
//-----------------------------------------------------------------------------
const getSparkleAssets = (id: BlockId) => {
  const assets = [];

  switch (id) {
    case 'basicBlue':
      assets.push('assets/particles/sparkle_blue.png');
      break;
    case 'basicGreen':
      assets.push('assets/particles/sparkle_green.png');
      break;
    case 'basicPurple':
      assets.push('assets/particles/sparkle_purple.png');
      break;
    case 'basicRed':
      assets.push('assets/particles/sparkle_red.png');
      break;
    case 'basicYellow':
      assets.push('assets/particles/sparkle_yellow.png');
      break;
    default:
      assets.push(
        'assets/particles/sparkle_blue.png',
        'assets/particles/sparkle_green.png',
        'assets/particles/sparkle_purple.png',
        'assets/particles/sparkle_red.png',
        'assets/particles/sparkle_yellow.png',
      );
  }

  return assets;
};

/*
    power block spawn effect from merging and extra spawns
*/
export class PowerBlockSpawnEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _entity: BlockEntity;
  private readonly _view: Container;
  private readonly _glowView: Container;
  private _sparkleAssets: string[];

  // init
  //-------------------------------------------------------------------------
  constructor(
    entity: BlockEntity,
    blockId: BlockId,
    private readonly powerBlockId: BlockId,
  ) {
    this._entity = entity;
    this._glowView = entity.c.view2d.view.getChildAt(0) as Container;
    this._view = entity.c.view2d.view.getChildAt(1) as Container;
    this._sparkleAssets = getSparkleAssets(blockId);
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const scene = this._entity.scene as GameScene;

    // Bring to front
    setBlockEntityViewLayer(scene, this._entity, 'front');

    // play sound
    app.sound.play('puzzle-power-block-spawn', {
      volume: 0.4,
      pan: mapGetPan(this._entity.c.position.mapPosition),
    });

    // Animate with movieclip if supported.
    // Bomb animation.
    if (this.powerBlockId === 'bomb') {
      const { animation } = (this._entity as BombBlockEntity).c.blockBomb.view;
      void animation
        .playAsync('waterballoon_spawn')
        .then(() => setBlockEntityViewLayer(scene, this._entity, 'default'));

      return;
    }

    // Rocket animation.
    if (['rocketHorizontal', 'rocketVertical'].includes(this.powerBlockId)) {
      const { animation } = (this._entity as RocketBlockEntity).c.blockRocket
        .view;
      void animation
        .playAsync('rocket_spawn')
        .then(() => setBlockEntityViewLayer(scene, this._entity, 'default'));

      return;
    }

    this._animateSparkles().then(() =>
      setBlockEntityViewLayer(scene, this._entity, 'default'),
    );

    await gsap.fromTo(
      this._view,
      { scaleX: 0, scaleY: 0 },
      {
        duration: 0.2,
        scaleX: 1.3,
        scaleY: 1.3,
        ease: 'quad.out',
      },
    );

    gsap.to(this._view, {
      duration: 0.15,
      scaleX: 1,
      scaleY: 1,
      ease: 'quad.inOut',
    });
  }

  private async _animateSparkles() {
    const emitter = app.particles.create({
      textures: this._sparkleAssets,
      rate: 0.02,
      limit: 50,
      emitterDuration: 0.2,
      scale: [
        { x: 1.2, y: 1.2 },
        { x: 1.3, y: 1.3 },
      ],
      duration: 0.4,
      behaviors: [
        { type: 'explode', magnitude: [300, 350] },
        { type: 'kinematic' },
        { type: 'fade', to: 0, tween: tweenPower2In },
        { type: 'scale', to: 0, tween: tweenPower2In },
      ],
    });

    // spawn emitter
    this._glowView.addChild(emitter.view);

    // aniamte
    await emitter.start();

    // despawn
    this._glowView.removeChild(emitter.view);
  }
}
